import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { ThemeClasses, PointOfInterest } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { APIProvider } from "@vis.gl/react-google-maps";
import { PointOfInterestMapPreview } from "./PointOfInterestMapPreview";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../../../../../hooks/useIsReseller";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface PointOfInterestDetailsModalProps {
  isOpen: boolean;
  poi: PointOfInterest | null;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit?: (poi: PointOfInterest) => void;
}

const getStatusColorByName = (statusName?: string): string => {
  const name = statusName?.toLowerCase() || "";
  if (name.includes("activ") && !name.includes("inactiv")) {
    return "text-primary-500 bg-primary-500/10";
  }
  if (name.includes("inactiv")) {
    return "text-red-500 bg-red-500/10";
  }
  return "text-gray-500 bg-gray-500/10";
};

const getCategoryLabel = (
  categoryId: string | undefined,
  t: TFunction<"adminSections">,
): string => {
  if (!categoryId) return t("pointsOfInterest.categories.other");
  return t(`pointsOfInterest.categories.${categoryId}`, t("pointsOfInterest.categories.other"));
};

export function PointOfInterestDetailsModal({
  isOpen,
  poi,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
}: PointOfInterestDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const disableCRUD = useDisableCRUD();

  // Obtener información del usuario para permisos
  const userId = localStorage.getItem("userId") || "";
  const userRoleId = localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;

  // Verificar si puede editar: reseller puede todo, carrier solo sus creaciones
  const canEdit = isReseller || poi?.createdBy === userId;

  // Obtener el status del POI desde el store
  const poiStatus = statuses.find((s) => s.id === poi?.status_id);

  if (!isOpen || !poi) return null;

  const markerPosition = poi.location ? {
    lat: poi.location.latitude,
    lng: poi.location.longitude,
  } : null;

  const inputClassName = `w-full text-xs 2xl:text-base px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-100"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  return (
    <Modal
      title={poi.name}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-[90vw] max-w-4xl"
      showEditButton={canEdit && !!onEdit}
      onEdit={() => onEdit?.(poi)}
      disableCRUD={disableCRUD}
      content={
        <APIProvider
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
        >
          <div className="flex flex-col gap-6">
            {/* Mapa */}
            <div className="w-full h-[250px] rounded-xl overflow-hidden border border-gray-200 dark:border-dark-border">
              <PointOfInterestMapPreview
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                mapCenter={markerPosition || { lat: 19.4326, lng: -99.1332 }}
                markerPosition={markerPosition}
                onMarkerPositionChange={() => {}}
                readOnly={true}
              />
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.name")}
                </label>
                <div className={inputClassName}>{poi.name}</div>
              </div>

              {/* Estado */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.status")}
                </label>
                <div className="flex items-center">
                  <span className={`text-sm px-3 py-1.5 rounded-full ${getStatusColorByName(poiStatus?.name)}`}>
                    {poiStatus?.name
                      ? getStatusHumanLabel(poiStatus.name, t)
                      : t("pointsOfInterest.status.unknown")}
                  </span>
                </div>
              </div>

              {/* Latitud */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.latitude")}
                </label>
                <div className={inputClassName}>
                  {poi.location?.latitude?.toFixed(6) || "-"}
                </div>
              </div>

              {/* Longitud */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.longitude")}
                </label>
                <div className={inputClassName}>
                  {poi.location?.longitude?.toFixed(6) || "-"}
                </div>
              </div>

              {/* Dirección */}
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.address")}
                </label>
                <div className={inputClassName}>
                  {poi.address || t("pointsOfInterest.noAddress")}
                </div>
              </div>

              {/* Visibilidad */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.visibility")}
                </label>
                <div className={`${inputClassName} flex items-center gap-2`}>
                  <i className={`${poi.shared ? "ri-group-line text-blue-500" : "ri-lock-line text-amber-500"}`}></i>
                  <span>{poi.shared ? t("pointsOfInterest.visibility.shared") : t("pointsOfInterest.visibility.private")}</span>
                </div>
              </div>

              {/* Categoría */}
              <div className="flex flex-col gap-1">
                <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                  {t("pointsOfInterest.fields.category")}
                </label>
                <div className={inputClassName}>
                  {getCategoryLabel(poi.category_id, t)}
                </div>
              </div>

              {/* Notas */}
              {poi.notes && (
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className={`text-xs font-medium ${themeClasses.textSecondary}`}>
                    {t("pointsOfInterest.fields.notes")}
                  </label>
                  <div className={`${inputClassName} min-h-[60px]`}>
                    {poi.notes}
                  </div>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className={`${canEdit && onEdit ? 'flex-1' : 'w-full'} text-xs 2xl:text-base py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-full ${themeClasses.buttonText} font-medium transition-colors`}
              >
                {t("pointsOfInterest.actions.close")}
              </button>
              {canEdit && onEdit && (
                <CRUDTooltip className="flex-1">
                  <button
                    type="button"
                    onClick={() => onEdit(poi)}
                    disabled={disableCRUD}
                    className="w-full text-xs 2xl:text-base py-3 px-4 bg-primary-500 hover:bg-primary-400 text-white rounded-full font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("pointsOfInterest.actions.edit")}
                  </button>
                </CRUDTooltip>
              )}
            </div>
          </div>
        </APIProvider>
      }
    />
  );
}
